import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

// Type definitions for fax API
export interface SendFaxFromBlobRequest {
  recipient_number: string;
  blob_id: string;
  request_type?: 'treatment_request' | 'general_document';
  sender_name?: string;
  cover_message?: string;
  file_name?: string;
  request_id?: string;
  matter_id?: string;
  entity_name?: string;
  document_type?: string;
  additional_metadata?: Record<string, string>;
}

export interface FaxTransmissionResponse {
  transmission_id: string;
  fax_transmission_id: string;
  status: string;
  message: string;
}

export interface SendFaxFromDocumentInstanceRequest {
  recipient_number: string;
  matter_id: string;
  document_instance_id: string;
  request_type?: 'treatment_request' | 'general_document';
  sender_name?: string;
  cover_message?: string;
  file_name?: string;
  entity_name?: string;
  document_type?: string;
  additional_metadata?: Record<string, string>;
}

/**
 * Request for sending multiple documents merged into a single fax.
 * Documents are merged in order: document instances first, then blobs.
 */
export interface SendMergedFaxRequest {
  recipient_number: string;
  matter_id: string;
  /** Document instance IDs to include (rendered to PDF) */
  document_instance_ids?: string[];
  /** Blob IDs to include (must be PDFs) */
  blob_ids?: string[];
  request_type?: 'treatment_request' | 'general_document';
  sender_name?: string;
  cover_message?: string;
  cover_page_from?: string;
  cover_page_to?: string;
  file_name?: string;
  entity_name?: string;
  document_type?: string;
  additional_metadata?: Record<string, string>;
}

export interface FaxTransmission {
  id: string;
  transmission_id: string;
  status: string;
  recipient_number: string;
  created_at: string;
  sent_at?: string;
  delivered_at?: string;
  updated_at: string;
  file_url?: string;
  file_name?: string;
}

export const faxService = createApi({
  reducerPath: 'faxApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['FaxTransmission'] as const,
  endpoints: (builder) => ({
    // Send fax using blob ID
    sendFaxFromBlob: builder.mutation<
      FaxTransmissionResponse,
      SendFaxFromBlobRequest
    >({
      query: (body) => ({
        url: '/api/fax-transmissions/send_fax_from_blob/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FaxTransmission'],
    }),

    // Send fax using a document instance ID
    sendFaxFromDocumentInstance: builder.mutation<
      FaxTransmissionResponse,
      SendFaxFromDocumentInstanceRequest
    >({
      query: (body) => ({
        url: '/api/fax-transmissions/send_fax_from_document_instance/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FaxTransmission'],
    }),

    // Send merged fax from multiple documents (document instances + blobs)
    sendMergedFax: builder.mutation<
      FaxTransmissionResponse,
      SendMergedFaxRequest
    >({
      query: (body) => ({
        url: '/api/fax-transmissions/send_merged_fax/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FaxTransmission'],
    }),

    // List fax transmissions
    listFaxTransmissions: builder.query<FaxTransmission[], void>({
      query: () => '/api/fax-transmissions/',
      providesTags: ['FaxTransmission'],
    }),

    // List fax transmissions by matter
    listFaxTransmissionsByMatter: builder.query<
      {
        transmissions: FaxTransmission[];
        count: number;
      },
      string
    >({
      query: (matterId) => `/api/fax-transmissions/?matter_id=${matterId}`,
      providesTags: (result, error, matterId) => [
        { type: 'FaxTransmission', id: matterId },
        'FaxTransmission',
      ],
    }),

    // Get specific fax transmission
    getFaxTransmission: builder.query<FaxTransmission, string>({
      query: (id) => `/api/fax-transmissions/${id}/`,
      providesTags: (result, error, id) => [{ type: 'FaxTransmission', id }],
    }),

    // Refresh transmission status from HelloSign
    refreshFaxStatus: builder.mutation<
      { transmission: FaxTransmission; message: string },
      string
    >({
      query: (id) => ({
        url: `/api/fax-transmissions/${id}/refresh_status/`,
        method: 'POST',
      }),
      // Mock response for demo - simulate status progression
      transformResponse: (response: any, _meta, arg) => {
        // For demo purposes, simulate a progressive status based on time since creation
        // Store creation times in sessionStorage to maintain state across polls
        const storageKey = `fax_demo_${arg}`;
        let createdTime = sessionStorage.getItem(storageKey);

        if (!createdTime) {
          createdTime = Date.now().toString();
          sessionStorage.setItem(storageKey, createdTime);
        }

        const elapsed = Date.now() - parseInt(createdTime, 10);
        let status: string;

        // Progress through statuses based on elapsed time
        if (elapsed < 10000) {
          status = 'pending';
        } else if (elapsed < 20000) {
          status = 'sent';
        } else {
          status = 'delivered';
          // Clean up storage after delivery
          sessionStorage.removeItem(storageKey);
        }

        // Create a mock response that simulates status progression
        const mockTransmission: FaxTransmission = response?.transmission || {
          id: arg,
          transmission_id: `mock_${arg}`,
          status: status as any,
          recipient_number:
            response?.transmission?.recipient_number || '+12125550198',
          created_at: new Date(parseInt(createdTime, 10)).toISOString(),
          updated_at: new Date().toISOString(),
        };

        return {
          transmission: mockTransmission,
          message: `Status updated to ${mockTransmission.status} (demo mode)`,
        };
      },
      invalidatesTags: (result, error, id) => [
        { type: 'FaxTransmission', id },
        'FaxTransmission',
      ],
    }),
  }),
});

export const {
  useSendFaxFromBlobMutation,
  useSendFaxFromDocumentInstanceMutation,
  useSendMergedFaxMutation,
  useListFaxTransmissionsQuery,
  useListFaxTransmissionsByMatterQuery,
  useGetFaxTransmissionQuery,
  useRefreshFaxStatusMutation,
} = faxService;
